import type { ExperienceVariable } from '@matter_extractor/shared';
import type { ReactNode } from 'react';
import { READER_TEST_IDS } from '../contracts';
import { READER_VARIABLE_CLASS, readerPlainText, readerPreviewSegments } from '../reader-text';

export interface VariableTextProps {
  readonly text: string;
  readonly resolveVariable: ((variable: ExperienceVariable) => string | null) | undefined;
  /** A preview NAMES its variables rather than resolving them (D4, D5) - see `reader-text.ts`. */
  readonly variableNames: ((variable: ExperienceVariable) => string) | undefined;
}

/**
 * A PLAIN text of a block, its variables resolved or named (§8.6; grill
 * Variables of 2026-09-16, D4, D5).
 *
 * The arbitration is `reader-text.ts`'s and is stated there: a session
 * RESOLVES, a preview NAMES and draws each token as a pill in the theme's
 * accent. What is here is only the JSX of that answer, and it lives in its own
 * file because the two blocks of issue #137 - a Lien's label, a Texte
 * copiable's text - both need it and neither owns it. Two spellings of one pill
 * would be a token drawn one way on a link and another on a copiable, on one
 * page, side by side.
 *
 * No `dangerouslySetInnerHTML` anywhere near it: a plain text has no marks, so
 * React owns the markup and the pill is a `<span>` - the same shape
 * `checkbox-block.tsx` has drawn since the grill. That component predates this
 * file and still carries its own copy; folding it in is a gesture for whoever
 * next opens it, not for this lot.
 */
export function VariableText({
  text,
  resolveVariable,
  variableNames,
}: VariableTextProps): ReactNode {
  if (resolveVariable !== undefined || variableNames === undefined) {
    return readerPlainText(text, resolveVariable);
  }
  return readerPreviewSegments(text, variableNames).map((segment, index) =>
    segment.kind === 'text' ? (
      // biome-ignore lint/suspicious/noArrayIndexKey: a run of literal text has no id, and two identical runs are two distinct places in one text
      <span key={`text-${index}`}>{segment.text}</span>
    ) : (
      <span
        // biome-ignore lint/suspicious/noArrayIndexKey: the same token may appear twice, and each occurrence is its own pill
        key={`variable-${index}`}
        className={READER_VARIABLE_CLASS}
        data-testid={READER_TEST_IDS.variable}
      >
        {segment.label}
      </span>
    ),
  );
}
